import { Grid } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import {
  checkVideoPlatformTokenExists,
  checkVideoPlatformTokenExistsSuccess,
  getGoogleMeetAuthURI,
  getZoomAuthURI,
  resetCheckVideoPlatformTokenExists,
  resetDeleteVideoPlatform,
  resetSaveLinkedVideoCallPlatform,
  resetVideoPlatforAuthURI,
  saveLinkedVideoPlatform,
  VideoCallPlatform,
} from '../../../redux/booking/bookingSlice';
import { useAppSelector } from '../../../redux/hooks';
import { TokenTypeEnum } from '../../../types/booking';
import LoadingCalendarOption from './LoadingCalendarOption';
import MeetingrOption from '../MeetingsOption';

const videoCallPlatformOptions = [
  {
    id: 1,
    icon: '/assets/icons/logos-google-meet.svg',
    disableIcon: '/assets/icons/google-meet-faded.svg',
    text: 'Google Meet',
  },
  // temporarily removing Zoom until app is approved
  // {
  //   id: 2,
  //   icon: '/assets/icons/zoom-icon.svg',
  //   disableIcon: '/assets/icons/logos-zoom.svg',
  //   text: 'Zoom',
  // },
];

function LinkedVideoPlatform() {
  const dispatch = useDispatch();
  const {
    checkVideoPlatformTokenExistsLoading,
    coachConfigurations,
    googleMeetAuthURI,
    deleteVideoPlatformLoading,
    deleteVideoPlatformSuccess,
    coachConfigurationsLoading,
    videoPlatformUsername,
    zoomAuthURI,
    googleMeetAuthURISuccess,
    zoomAuthURISuccess,
    isVideoPlatformTokenExists,
    linkedVideoCallPlatformSuccess,
    zoomAuthURIFailure,
    googleMeetAuthURIFailure,
    checkVideoPlatformTokenExistsFailure,
    linkedVideoCallPlatformLoading,
  } = useAppSelector((state) => state.booking);
  const coachId = useAppSelector((state) => state.coach.coach.id);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const authWindowRef = useRef<Window | null>(null);

  useEffect(() => {
    if (zoomAuthURIFailure || googleMeetAuthURIFailure || checkVideoPlatformTokenExistsFailure) {
      setSelectedOption(null);
    }
  }, [zoomAuthURIFailure, googleMeetAuthURIFailure, checkVideoPlatformTokenExistsFailure]);

  useEffect(() => {
    if (coachConfigurations.videoCallPlatform === VideoCallPlatform.GOOGLE_MEET) {
      setSelectedOption(1);
      dispatch(checkVideoPlatformTokenExistsSuccess({ isTokenExist: true }));
    }
    if (coachConfigurations.videoCallPlatform === VideoCallPlatform.ZOOM) {
      setSelectedOption(2);
      dispatch(checkVideoPlatformTokenExistsSuccess({ isTokenExist: true }));
    }
  }, [coachConfigurations.videoCallPlatform]);

  useEffect(() => {
    if (googleMeetAuthURI && !authWindowRef.current) {
      authWindowRef.current = window.open(
        googleMeetAuthURI,
        '_blank',
        'location=yes,height=570,width=520,scrollbars=yes,status=yes'
      );

      const checkWindowClosed = setInterval(() => {
        if (authWindowRef.current?.closed) {
          clearInterval(checkWindowClosed);
          authWindowRef.current = null;
          dispatch(
            checkVideoPlatformTokenExists({ coachId, tokenType: TokenTypeEnum.GOOGLE_CALENDER })
          );
        }
      }, 1000);

      return () => {
        clearInterval(checkWindowClosed);
        if (authWindowRef.current) {
          authWindowRef.current.close();
          authWindowRef.current = null;
        }
      };
    }
  }, [googleMeetAuthURI]);

  useEffect(() => {
    if (zoomAuthURI && !authWindowRef.current) {
      authWindowRef.current = window.open(
        zoomAuthURI,
        '_blank',
        'location=yes,height=570,width=520,scrollbars=yes,status=yes'
      );

      const checkWindowClosed = setInterval(() => {
        if (authWindowRef.current?.closed) {
          clearInterval(checkWindowClosed);
          authWindowRef.current = null;
          dispatch(checkVideoPlatformTokenExists({ coachId, tokenType: TokenTypeEnum.ZOOM }));
        }
      }, 1000);

      return () => {
        clearInterval(checkWindowClosed);
        if (authWindowRef.current) {
          authWindowRef.current.close();
          authWindowRef.current = null;
        }
      };
    }
  }, [zoomAuthURI]);

  useEffect(() => {
    if (googleMeetAuthURISuccess && isVideoPlatformTokenExists && videoPlatformUsername) {
      if (selectedOption === 1) {
        dispatch(
          saveLinkedVideoPlatform({
            coachId,
            videoCallPlatform: VideoCallPlatform.GOOGLE_MEET,
            videoCallPlatformUsername: videoPlatformUsername,
          })
        );
      }
    }
    if (zoomAuthURISuccess && isVideoPlatformTokenExists && videoPlatformUsername) {
      if (selectedOption === 2) {
        dispatch(
          saveLinkedVideoPlatform({
            coachId,
            videoCallPlatform: VideoCallPlatform.ZOOM,
            videoCallPlatformUsername: videoPlatformUsername,
          })
        );
      }
    }
  }, [
    googleMeetAuthURISuccess,
    isVideoPlatformTokenExists,
    zoomAuthURISuccess,
    videoPlatformUsername,
  ]);

  useEffect(() => {
    if (linkedVideoCallPlatformSuccess) {
      dispatch(resetSaveLinkedVideoCallPlatform());
      dispatch(resetVideoPlatforAuthURI());
    }
  }, [linkedVideoCallPlatformSuccess]);

  const handleSelectVideoOption = (optionId: number | null) => {
    setSelectedOption(optionId);
    if (optionId === 1) {
      dispatch(
        getGoogleMeetAuthURI({
          calendar: coachConfigurations.calendar,
          calendarUsername: coachConfigurations.calendarUsername,
          coachId,
        })
      );
    }
    if (optionId === 2) {
      dispatch(getZoomAuthURI());
    }
    dispatch(resetDeleteVideoPlatform());
    dispatch(resetSaveLinkedVideoCallPlatform());
  };

  useEffect(
    () => () => {
      dispatch(resetVideoPlatforAuthURI());
      dispatch(resetSaveLinkedVideoCallPlatform());
      dispatch(resetCheckVideoPlatformTokenExists());
      setSelectedOption(null);
    },
    [dispatch]
  );

  if (coachConfigurationsLoading) {
    return (
      <Grid
        display="flex"
        flexDirection="column"
        alignItems="center"
        width="100%"
        height="calc(100% - 68px)"
        boxShadow="0px 4px 8px #FFDDC859"
        borderRadius="10px"
        p={3}
      >
        <Grid
          display="flex"
          justifyContent="center"
          m="auto"
          gap={2}
          width="100%"
          flexDirection="column"
          alignItems="center"
        >
          {videoCallPlatformOptions.map((option) => (
            <LoadingCalendarOption key={option.id} />
          ))}
        </Grid>
      </Grid>
    );
  }
  return (
    <Grid
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
      height="calc(100% - 68px)"
      boxShadow="0px 4px 8px #FFDDC859"
      borderRadius="10px"
      gap={2}
      p={3}
    >
      <Grid
        display="flex"
        justifyContent="center"
        m="auto"
        gap={2}
        width="100%"
        flexDirection="column"
        alignItems="center"
      >
        {videoCallPlatformOptions.map((option) => (
          <MeetingrOption
            key={option.id}
            icon={option.icon}
            text={option.text}
            id={option.id}
            disableIcon={option.disableIcon}
            selectedOption={selectedOption}
            onSelect={handleSelectVideoOption}
            loading={checkVideoPlatformTokenExistsLoading || linkedVideoCallPlatformLoading}
            deleteLoading={deleteVideoPlatformLoading}
            deleteSuccess={deleteVideoPlatformSuccess}
            email={coachConfigurations.videoCallPlatformUsername}
          />
        ))}
      </Grid>
    </Grid>
  );
}

export default LinkedVideoPlatform;
